import { useMemo, useState, useCallback } from "react";
import { eachDayOfInterval, format, isValid, parseISO } from "date-fns";
import {
  Briefcase,
  Copy,
  FolderPlus,
  Headphones,
  MoreHorizontal,
  Users,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { UtensilsCrossed, FileDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ProjectWithRelations, useUpdateProject } from "@/hooks/useProjects";
import {
  AvailabilityStatus,
  useBatchUpdateAvailability,
  useBulkAssignFunctionSeries,
  useCreateFunctionAssignment,
  useCopyProjectFunctions,
  useCreateProjectFunctionGroup,
  useCreateProjectFunctionSeries,
  useDeleteAvailability,
  useDeleteFunctionAssignment,
  useDeleteProjectPersonTravelDay,
  useDeleteProjectFunctionSeries,
  useInitializeProjectFunctions,
  useProjectAvailability,
  useProjectFunctionGroups,
  useProjectFunctions,
  useProjectPersonTravelDays,
  useUpdateProjectFunctionGroup,
  useUpdateFunctionAssignment,
  useUpdateProjectFunctionDay,
  useUpdateProjectFunctionSeries,
  useUpsertAvailability,
  useUpsertProjectPersonTravelDay,
  useUpdateAvailabilityRemark,
  useUpdateAvailabilityUnityUsername,
} from "@/hooks/useProjectPeople";
import { AddPersonDialog } from "./people/AddPersonDialog";
import { AvailabilityTable } from "./people/AvailabilityTable";
import { AddFunctionDialog } from "./people/AddFunctionDialog";
import { SendCrewEmailDialog } from "./people/SendCrewEmailDialog";
import { CrewEmailHistory } from "./people/CrewEmailHistory";
import {
  FunctionAssignmentMatrix,
  type FunctionViewMode,
} from "./people/FunctionAssignmentMatrix";
import { MealHotelPlanningTable } from "./people/MealHotelPlanningTable";
import {
  useProjectMealPlans,
  useProjectHotelPlans,
  useUpsertMealPlan,
  useUpsertHotelPlan,
  useBatchUpsertMealPlans,
  useBatchUpsertHotelPlans,
} from "@/hooks/useProjectPlanning";
import {
  generateCrewListPDF,
  CrewListPDFOptions,
} from "@/lib/crew-list-pdf-generator";
import { useCameraAssignmentMap } from "@/hooks/useCameraPlans";
import { ExportCrewListDialog } from "./people/ExportCrewListDialog";
import { InitializeProjectFunctionsDialog } from "./people/InitializeProjectFunctionsDialog";
import { PeopleOverviewTable } from "./people/PeopleOverviewTable";
import { IntercomTab } from "./people/IntercomTab";
import { useProjectIntercomAssignments } from "@/hooks/useProjectIntercom";
import { useSingleStudio } from "@/hooks/useStudios";
import { toast } from "sonner";
import { formatProjectName } from "@/lib/format-project-name";
import { Eye } from "lucide-react";
import { CopyFromProjectSelector } from "./CopyFromProjectSelector";

interface ProjectPeopleTabProps {
  project: ProjectWithRelations;
  currentSubtab?: string;
  onSubtabChange?: (value: string) => void;
}

const DEFAULT_PROJECT_FUNCTION_GROUPS = [
  { name: "Studio", display_order: 0 },
  { name: "Camera", display_order: 1 },
] as const;
type ProjectFunctionGroupTemplate = {
  name: string;
  display_order: number;
};

function getProjectDays(startStr?: string | null, endStr?: string | null) {
  if (!startStr || !endStr) return [];
  const start = parseISO(startStr);
  const end = parseISO(endStr);

  if (!isValid(start) || !isValid(end) || start > end) return [];
  return eachDayOfInterval({ start, end });
}

export function ProjectPeopleTab({
  project,
  currentSubtab = "overview",
  onSubtabChange,
}: ProjectPeopleTabProps) {
  const projectDisplayName = formatProjectName(
    project.name,
    project.event_start_date,
  );
  const [functionViewMode, setFunctionViewMode] =
    useState<FunctionViewMode>("function");
  const [functionDayFilter, setFunctionDayFilter] = useState<
    "planning" | "event"
  >("planning");
  const [copyFunctionsOpen, setCopyFunctionsOpen] = useState(false);
  const [sourceProjectId, setSourceProjectId] = useState<string | null>(
    project.previous_edition_project_id ?? null,
  );

  const { data: availability, isLoading: loadingAvailability } =
    useProjectAvailability(project.id);
  const { data: functions, isLoading: loadingFunctions } = useProjectFunctions(
    project.id,
  );
  const { data: functionGroups, isLoading: loadingFunctionGroups } =
    useProjectFunctionGroups(project.id);
  const { data: travelDays, isLoading: loadingTravelDays } =
    useProjectPersonTravelDays(project.id);
  const { data: sourceFunctions = [], isLoading: loadingSourceFunctions } =
    useProjectFunctions(sourceProjectId ?? undefined);
  const { data: mealPlans, isLoading: loadingMeals } = useProjectMealPlans(
    project.id,
  );
  const { data: hotelPlans, isLoading: loadingHotels } = useProjectHotelPlans(
    project.id,
  );
  const { data: intercomAssignments } = useProjectIntercomAssignments(
    project.id,
  );
  const { data: studio } = useSingleStudio(project.studio_id ?? undefined);
  const { data: cameraAssignmentMap } = useCameraAssignmentMap(project.id);

  const upsertAvailability = useUpsertAvailability();
  const batchUpdateAvailability = useBatchUpdateAvailability();
  const deleteAvailability = useDeleteAvailability();
  const createFunctionSeries = useCreateProjectFunctionSeries();
  const copyProjectFunctions = useCopyProjectFunctions();
  const createFunctionGroup = useCreateProjectFunctionGroup();
  const updateFunctionGroup = useUpdateProjectFunctionGroup();
  const updateFunctionSeries = useUpdateProjectFunctionSeries();
  const deleteFunctionSeries = useDeleteProjectFunctionSeries();
  const updateFunctionDay = useUpdateProjectFunctionDay();
  const upsertTravelDay = useUpsertProjectPersonTravelDay();
  const deleteTravelDay = useDeleteProjectPersonTravelDay();
  const createAssignment = useCreateFunctionAssignment();
  const bulkAssignSeries = useBulkAssignFunctionSeries();
  const deleteAssignment = useDeleteFunctionAssignment();
  const updateAssignment = useUpdateFunctionAssignment();
  const upsertMealPlan = useUpsertMealPlan();
  const upsertHotelPlan = useUpsertHotelPlan();
  const batchUpsertMeals = useBatchUpsertMealPlans();
  const batchUpsertHotels = useBatchUpsertHotelPlans();
  const updateProject = useUpdateProject();
  const updateAvailabilityRemark = useUpdateAvailabilityRemark();
  const updateAvailabilityUnityUsername = useUpdateAvailabilityUnityUsername();
  const initializeProjectFunctions = useInitializeProjectFunctions();

  // Project remarks state
  const [crewListRemarks, setCrewListRemarks] = useState(
    project.crew_list_remarks ?? "",
  );
  const [mealsRemarks, setMealsRemarks] = useState(project.meals_remarks ?? "");
  const [hotelsRemarks, setHotelsRemarks] = useState(
    project.hotels_remarks ?? "",
  );
  const [newFunctionGroupName, setNewFunctionGroupName] = useState("");

  const saveProjectRemarks = useCallback(
    (field: string, value: string) => {
      updateProject.mutate({
        id: project.id,
        updates: { [field]: value || null },
      });
    },
    [project.id, updateProject],
  );

  const planningDays = useMemo(
    () =>
      getProjectDays(
        project.planning_start_date || project.event_start_date,
        project.planning_end_date || project.event_end_date,
      ),
    [
      project.planning_end_date,
      project.planning_start_date,
      project.event_end_date,
      project.event_start_date,
    ],
  );

  const eventDays = useMemo(
    () => getProjectDays(project.event_start_date, project.event_end_date),
    [project.event_end_date, project.event_start_date],
  );
  const visibleFunctionDays = useMemo(
    () => (functionDayFilter === "event" ? eventDays : planningDays),
    [eventDays, functionDayFilter, planningDays],
  );

  const existingPersonIds = useMemo(
    () => availability?.map((a) => a.person_id) || [],
    [availability],
  );
  const functionDaysById = useMemo(
    () =>
      new Map(
        (functions || [])
          .flatMap((series) => series.days)
          .map((day) => [day.id, day.function_date] as const),
      ),
    [functions],
  );

  const handleAddPeople = (
    personIds: string[],
    status: AvailabilityStatus,
    dateType: "planning" | "event" | "custom",
    customDates?: string[],
  ) => {
    const allDays =
      dateType === "custom" && customDates
        ? customDates
        : (dateType === "event" ? eventDays : planningDays).map((day) =>
            format(day, "yyyy-MM-dd"),
          );
    personIds.forEach((personId) => {
      upsertAvailability.mutate({
        project_id: project.id,
        person_id: personId,
        status,
        available_days: allDays,
      });
    });
  };

  const handleUpdateStatus = (
    personId: string,
    status: AvailabilityStatus,
    days: string[],
  ) => {
    upsertAvailability.mutate({
      project_id: project.id,
      person_id: personId,
      status,
      available_days: days,
    });
  };

  const handleBatchUpdate = (
    personIds: string[],
    status: AvailabilityStatus,
    days: string[],
  ) => {
    batchUpdateAvailability.mutate({
      projectId: project.id,
      personIds,
      status,
      days,
    });
  };

  const handleRemovePerson = (id: string) => {
    deleteAvailability.mutate({ id, projectId: project.id });
  };

  const handleUpdateCrewListRemark = (
    availabilityId: string,
    remark: string,
  ) => {
    updateAvailabilityRemark.mutate({
      id: availabilityId,
      crewListRemark: remark || null,
      projectId: project.id,
    });
  };

  const handleUpdateAvailabilityUnityUsername = (
    availabilityId: string,
    unityUsername: string,
  ) => {
    updateAvailabilityUnityUsername.mutate({
      id: availabilityId,
      unityUsername: unityUsername || null,
      projectId: project.id,
    });
  };

  const handleAddFunction = (data: {
    name: string;
    project_function_group_id?: string | null;
    required_count: number;
    selected_days: string[];
    system_tag?: string | null;
    location_type?: import("@/hooks/useProjectPeople").LocationType;
  }) => {
    createFunctionSeries.mutate({
      project_id: project.id,
      ...data,
    });
  };

  const handleCreateFunctionGroup = () => {
    const name = newFunctionGroupName.trim();
    if (!name) return;
    const maxOrder =
      functionGroups?.reduce(
        (max, group) => Math.max(max, group.display_order),
        -1,
      ) ?? -1;
    createFunctionGroup
      .mutateAsync({
        project_id: project.id,
        name,
        display_order: maxOrder + 1,
      })
      .then(() => setNewFunctionGroupName(""))
      .catch((error) =>
        toast.error("Failed to create function group: " + error.message),
      );
  };

  const handleInitializeProjectFunctions = (
    functionsToCreate: {
      name: string;
      project_function_group_id?: string | null;
      project_function_group_name?: string | null;
      project_function_group_location_type?: import("@/hooks/useProjectPeople").LocationType;
      required_count: number;
      selected_days: string[];
      system_tag: string | null;
      location_type?: import("@/hooks/useProjectPeople").LocationType;
      day_overrides: { date: string; count: number }[];
    }[],
  ) => {
    void (async () => {
      const groupIdByName = new Map(
        (functionGroups || []).map((group) => [group.name, group.id] as const),
      );
      const groupDetailsByName = new Map(
        (functionGroups || []).map((group) => [group.name, group] as const),
      );
      const groupTemplatesByName = new Map<
        string,
        ProjectFunctionGroupTemplate
      >(DEFAULT_PROJECT_FUNCTION_GROUPS.map((group) => [group.name, group]));

      for (const fn of functionsToCreate) {
        if (fn.project_function_group_name) {
          groupTemplatesByName.set(fn.project_function_group_name, {
            name: fn.project_function_group_name,
            display_order:
              groupTemplatesByName.get(fn.project_function_group_name)
                ?.display_order ?? groupTemplatesByName.size,
          });
        }
      }

      for (const template of groupTemplatesByName.values()) {
        if (!groupIdByName.has(template.name)) {
          const group = await createFunctionGroup.mutateAsync({
            project_id: project.id,
            name: template.name,
            location_type:
              functionsToCreate.find(
                (fn) => fn.project_function_group_name === template.name,
              )?.project_function_group_location_type ?? null,
            display_order: template.display_order,
          });
          groupIdByName.set(group.name, group.id);
          groupDetailsByName.set(group.name, group);
        }
      }

      const functionsWithGroups = [];

      for (const fn of functionsToCreate) {
        let groupId = fn.project_function_group_id ?? null;
        if (!groupId && fn.project_function_group_name) {
          groupId = groupIdByName.get(fn.project_function_group_name) ?? null;
          if (!groupId) {
            const group = await createFunctionGroup.mutateAsync({
              project_id: project.id,
              name: fn.project_function_group_name,
              location_type: fn.project_function_group_location_type ?? null,
              display_order:
                groupDetailsByName.size +
                DEFAULT_PROJECT_FUNCTION_GROUPS.length,
            });
            groupId = group.id;
            groupIdByName.set(group.name, group.id);
            groupDetailsByName.set(group.name, group);
          }
        }
        functionsWithGroups.push({
          ...fn,
          project_function_group_id: groupId,
        });
      }

      initializeProjectFunctions.mutate({
        projectId: project.id,
        functions: functionsWithGroups,
      });
    })().catch((error) =>
      toast.error("Failed to initialize function groups: " + error.message),
    );
  };

  const handleUpdateFunctionGroup = (
    id: string,
    updates: {
      name?: string;
      location_type?: import("@/hooks/useProjectPeople").LocationType;
    },
  ) => {
    updateFunctionGroup
      .mutateAsync({ id, projectId: project.id, updates })
      .catch((error) =>
        toast.error("Failed to update function group: " + error.message),
      );
  };

  const handleUpdateSeries = (
    id: string,
    updates: {
      name?: string;
      project_function_group_id?: string | null;
      required_count?: number;
      selected_days?: string[];
      system_tag?: string | null;
      location_type?: import("@/hooks/useProjectPeople").LocationType;
    },
  ) => {
    updateFunctionSeries.mutate({ id, projectId: project.id, updates });
  };

  const handleDeleteSeries = (id: string) => {
    deleteFunctionSeries.mutate({ id, projectId: project.id });
  };

  const handleUpdateDayOverride = (
    dayId: string,
    requiredCountOverride: number | null,
  ) => {
    updateFunctionDay.mutate({
      id: dayId,
      projectId: project.id,
      requiredCountOverride,
    });
  };

  const handleAssign = (
    functionId: string,
    personId: string,
    remark?: string,
  ) => {
    createAssignment.mutate({
      functionId,
      personId,
      projectId: project.id,
      remark,
    });

    const personAvailability = availability?.find(
      (item) => item.person_id === personId,
    );
    const assignedDay = functionDaysById.get(functionId);
    if (personAvailability) {
      const nextStatus =
        personAvailability.status === "available"
          ? "booked"
          : personAvailability.status;
      const nextDays = assignedDay
        ? Array.from(
            new Set([
              ...(personAvailability.available_days || []),
              assignedDay,
            ]),
          ).sort()
        : personAvailability.available_days || [];
      upsertAvailability.mutate({
        project_id: project.id,
        person_id: personId,
        status: nextStatus,
        available_days: nextDays,
      });
    } else if (assignedDay) {
      upsertAvailability.mutate({
        project_id: project.id,
        person_id: personId,
        status: "booked",
        available_days: [assignedDay],
      });
    }
  };

  const handleBulkAssign = (
    _seriesId: string,
    personId: string,
    functionIds: string[],
  ) => {
    bulkAssignSeries.mutate({
      projectId: project.id,
      personId,
      functionIds,
    });

    const personAvailability = availability?.find(
      (item) => item.person_id === personId,
    );
    const assignedDays = functionIds
      .map((functionId) => functionDaysById.get(functionId))
      .filter((day): day is string => !!day);
    if (personAvailability) {
      const nextStatus =
        personAvailability.status === "available"
          ? "booked"
          : personAvailability.status;
      const nextDays = Array.from(
        new Set([
          ...(personAvailability.available_days || []),
          ...assignedDays,
        ]),
      ).sort();
      upsertAvailability.mutate({
        project_id: project.id,
        person_id: personId,
        status: nextStatus,
        available_days: nextDays,
      });
    } else if (assignedDays.length > 0) {
      upsertAvailability.mutate({
        project_id: project.id,
        person_id: personId,
        status: "booked",
        available_days: assignedDays,
      });
    }
  };

  const handleBookAssignedPerson = (personId: string, functionDate: string) => {
    const personAvailability = availability?.find(
      (item) => item.person_id === personId,
    );
    if (personAvailability) {
      const nextDays = Array.from(
        new Set([...(personAvailability.available_days || []), functionDate]),
      ).sort();
      upsertAvailability.mutate({
        project_id: project.id,
        person_id: personId,
        status: "booked",
        available_days: nextDays,
      });
      return;
    }

    upsertAvailability.mutate({
      project_id: project.id,
      person_id: personId,
      status: "booked",
      available_days: [functionDate],
    });
  };

  const handleUnassign = (assignmentId: string) => {
    deleteAssignment.mutate({ id: assignmentId, projectId: project.id });
  };

  const handleToggleTravelDay = (personId: string, date: string) => {
    const existing = travelDays?.find(
      (travelDay) =>
        travelDay.person_id === personId && travelDay.travel_date === date,
    );
    if (existing) {
      deleteTravelDay.mutate({ id: existing.id, projectId: project.id });
      return;
    }

    upsertTravelDay.mutate({
      project_id: project.id,
      person_id: personId,
      travel_date: date,
    });
  };

  const handleUpdateRemark = (assignmentId: string, remark: string | null) => {
    updateAssignment.mutate({
      id: assignmentId,
      remark,
      projectId: project.id,
    });
  };

  const handleUpdateMeal = (
    personId: string,
    date: string,
    field: "lunch" | "dinner",
    value: boolean,
  ) => {
    const existing = mealPlans?.find(
      (mp) => mp.person_id === personId && mp.plan_date === date,
    );
    upsertMealPlan.mutate({
      project_id: project.id,
      person_id: personId,
      plan_date: date,
      lunch: field === "lunch" ? value : (existing?.lunch ?? false),
      dinner: field === "dinner" ? value : (existing?.dinner ?? false),
    });
  };

  const handleUpdateHotel = (
    personId: string,
    date: string,
    value: boolean,
    roomType?: "single" | "double",
    roommatePersonId?: string | null,
    syncRoommatePair = false,
  ) => {
    const existing = hotelPlans?.find(
      (hp) => hp.person_id === personId && hp.plan_date === date,
    );
    const previousHotel = hotelPlans
      ?.filter(
        (hp) =>
          hp.person_id === personId && hp.needs_hotel && hp.plan_date < date,
      )
      .sort((a, b) => b.plan_date.localeCompare(a.plan_date))[0];
    const fallbackHotel = existing?.needs_hotel ? existing : previousHotel;
    const nextRoomType = value
      ? (roomType ?? fallbackHotel?.room_type ?? "single")
      : "single";
    const hasRoommateUpdate = roommatePersonId !== undefined;
    const nextRoommatePersonId =
      value && nextRoomType === "double"
        ? hasRoommateUpdate
          ? roommatePersonId
          : (fallbackHotel?.roommate_person_id ?? null)
        : null;
    const upsertHotel = (plan: {
      person_id: string;
      plan_date: string;
      needs_hotel: boolean;
      room_type: "single" | "double";
      roommate_person_id: string | null;
    }) =>
      upsertHotelPlan.mutate({
        project_id: project.id,
        ...plan,
      });

    const getHotel = (targetPersonId: string, targetDate: string) =>
      hotelPlans?.find(
        (hp) => hp.person_id === targetPersonId && hp.plan_date === targetDate,
      );

    const fillOpenRoommate = (
      targetPersonId: string,
      targetDate: string,
      targetRoommateId: string,
    ) => {
      const target = getHotel(targetPersonId, targetDate);
      if (
        target?.needs_hotel &&
        target.room_type === "double" &&
        !target.roommate_person_id
      ) {
        upsertHotel({
          person_id: targetPersonId,
          plan_date: targetDate,
          needs_hotel: true,
          room_type: "double",
          roommate_person_id: targetRoommateId,
        });
      }
    };

    upsertHotelPlan.mutate({
      project_id: project.id,
      person_id: personId,
      plan_date: date,
      needs_hotel: value,
      room_type: nextRoomType,
      roommate_person_id: nextRoommatePersonId,
    });

    if (
      value &&
      nextRoomType === "double" &&
      nextRoommatePersonId &&
      !hasRoommateUpdate
    ) {
      fillOpenRoommate(nextRoommatePersonId, date, personId);
    }

    if (!syncRoommatePair) return;

    if (
      existing?.roommate_person_id &&
      existing.roommate_person_id !== nextRoommatePersonId
    ) {
      upsertHotelPlan.mutate({
        project_id: project.id,
        person_id: existing.roommate_person_id,
        plan_date: date,
        needs_hotel: true,
        room_type: "double",
        roommate_person_id: null,
      });
    }

    if (nextRoomType === "double" && nextRoommatePersonId) {
      upsertHotel({
        person_id: nextRoommatePersonId,
        plan_date: date,
        needs_hotel: true,
        room_type: "double",
        roommate_person_id: personId,
      });

      hotelPlans
        ?.filter(
          (hp) =>
            hp.person_id === nextRoommatePersonId &&
            hp.plan_date !== date &&
            hp.needs_hotel &&
            hp.room_type === "double" &&
            !hp.roommate_person_id,
        )
        .forEach((roommateOpenPlan) => {
          const currentPersonPlan = getHotel(
            personId,
            roommateOpenPlan.plan_date,
          );
          if (
            !currentPersonPlan?.needs_hotel ||
            currentPersonPlan.room_type !== "double" ||
            currentPersonPlan.roommate_person_id
          ) {
            return;
          }

          upsertHotel({
            person_id: personId,
            plan_date: roommateOpenPlan.plan_date,
            needs_hotel: true,
            room_type: "double",
            roommate_person_id: nextRoommatePersonId,
          });
          upsertHotel({
            person_id: nextRoommatePersonId,
            plan_date: roommateOpenPlan.plan_date,
            needs_hotel: true,
            room_type: "double",
            roommate_person_id: personId,
          });
        });
    }
  };

  const handleInitFromFunctions = (personId?: string) => {
    if (!functions || !availability) return;

    const allDays = functions.flatMap((s) => s.days);

    const targetPeople = personId
      ? availability.filter((a) => a.person_id === personId)
      : availability.filter(
          (a) => a.status === "available" || a.status === "booked",
        );

    const mealBatch: {
      project_id: string;
      person_id: string;
      plan_date: string;
      lunch: boolean;
      dinner: boolean;
    }[] = [];
    const hotelBatch: {
      project_id: string;
      person_id: string;
      plan_date: string;
      needs_hotel: boolean;
      room_type: "single";
      roommate_person_id: null;
    }[] = [];

    targetPeople.forEach((avail) => {
      const assignedDates = new Set<string>();
      planningDays.forEach((day) => {
        const dateStr = format(day, "yyyy-MM-dd");
        const hasFunc = allDays.some(
          (fn) =>
            fn.function_date === dateStr &&
            fn.assignments?.some((a) => a.person_id === avail.person_id),
        );
        if (hasFunc) assignedDates.add(dateStr);
      });

      planningDays.forEach((day, idx) => {
        const dateStr = format(day, "yyyy-MM-dd");
        if (!assignedDates.has(dateStr)) return;

        const nextDateStr =
          idx < planningDays.length - 1
            ? format(planningDays[idx + 1], "yyyy-MM-dd")
            : null;
        const hasNextDay = nextDateStr && assignedDates.has(nextDateStr);

        mealBatch.push({
          project_id: project.id,
          person_id: avail.person_id,
          plan_date: dateStr,
          lunch: true,
          dinner: hasNextDay ? true : false,
        });
        hotelBatch.push({
          project_id: project.id,
          person_id: avail.person_id,
          plan_date: dateStr,
          needs_hotel: hasNextDay ? true : false,
          room_type: "single",
          roommate_person_id: null,
        });
      });
    });

    if (mealBatch.length > 0) {
      batchUpsertMeals.mutate({ projectId: project.id, plans: mealBatch });
    }
    if (hotelBatch.length > 0) {
      batchUpsertHotels.mutate({ projectId: project.id, plans: hotelBatch });
    }
    toast.success("Initialized meal & hotel plans from function assignments");
  };

  const openCopyFunctionsDialog = () => {
    setSourceProjectId(project.previous_edition_project_id ?? null);
    setCopyFunctionsOpen(true);
  };

  const handleCopyFunctions = async () => {
    if (!sourceProjectId || sourceFunctions.length === 0) return;

    try {
      await copyProjectFunctions.mutateAsync({
        targetProjectId: project.id,
        sourceProjectId,
      });
      setCopyFunctionsOpen(false);
      toast.success("Functions copied");
    } catch (error) {
      toast.error(
        `Failed to copy functions: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  };

  const handleExportPDF = async (options: CrewListPDFOptions) => {
    try {
      await generateCrewListPDF({
        project,
        availability: availability || [],
        functions: functions || [],
        functionGroups: functionGroups || [],
        mealPlans: mealPlans || [],
        hotelPlans: hotelPlans || [],
        intercomAssignments: intercomAssignments || [],
        intercomHostname: studio?.unity_hostname ?? null,
        travelDays: travelDays || [],
        cameraNumberByAssignmentId: cameraAssignmentMap || {},
        projectDays: planningDays,
        eventDays,
        options,
      });
      toast.success("Crew list PDF exported");
    } catch (e) {
      toast.error("Failed to export PDF: " + (e as Error).message);
    }
  };

  if (
    loadingAvailability ||
    loadingFunctions ||
    loadingFunctionGroups ||
    loadingTravelDays ||
    loadingMeals ||
    loadingHotels
  ) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (planningDays.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Users className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <h3 className="mb-2 text-lg font-medium text-foreground">
            Set Project Dates First
          </h3>
          <p className="mx-auto max-w-md text-muted-foreground">
            Please set the project start and end dates in the General tab before
            managing people and functions.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Tabs
      value={currentSubtab}
      onValueChange={onSubtabChange}
      className="min-w-0 space-y-4"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="w-full min-w-0 overflow-x-auto sm:w-auto">
          <TabsList className="min-w-max">
            <TabsTrigger value="overview" className="gap-2">
              <Eye className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger
              value="functions"
              className="gap-2"
              data-testid="functions-tab-trigger"
            >
              <Briefcase className="h-4 w-4" />
              Functions
            </TabsTrigger>
            <TabsTrigger value="availability" className="gap-2">
              <Users className="h-4 w-4" />
              Availability
            </TabsTrigger>
            <TabsTrigger value="planning" className="gap-2">
              <UtensilsCrossed className="h-4 w-4" />
              Meals & Hotel
            </TabsTrigger>
            <TabsTrigger value="intercom" className="gap-2">
              <Headphones className="h-4 w-4" />
              Intercom
            </TabsTrigger>
          </TabsList>
        </div>
        <ExportCrewListDialog onExport={handleExportPDF} />
      </div>

      <TabsContent value="overview" className="min-w-0">
        <PeopleOverviewTable
          availability={availability || []}
          functions={functions || []}
          groups={functionGroups || []}
          travelDays={travelDays || []}
          eventDays={eventDays}
          projectDays={planningDays}
          cameraNumberByAssignmentId={cameraAssignmentMap}
          onUpdateUnityUsername={handleUpdateAvailabilityUnityUsername}
        />
      </TabsContent>

      <TabsContent value="availability" className="min-w-0 space-y-4">
        <Card className="min-w-0">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <div>
              <CardTitle className="text-lg">Team Availability</CardTitle>
              <CardDescription>
                Manage who is available for this project and on which days.
              </CardDescription>
            </div>
            <AddPersonDialog
              existingPersonIds={existingPersonIds}
              planningDays={planningDays}
              eventDays={eventDays}
              onAdd={handleAddPeople}
              isLoading={upsertAvailability.isPending}
            />
          </CardHeader>
          <CardContent className="min-w-0">
            <AvailabilityTable
              availability={availability || []}
              projectDays={planningDays}
              eventDays={eventDays}
              planningDays={planningDays}
              onUpdateStatus={handleUpdateStatus}
              onBatchUpdate={handleBatchUpdate}
              onRemove={handleRemovePerson}
              onUpdateCrewListRemark={handleUpdateCrewListRemark}
              isUpdating={
                upsertAvailability.isPending ||
                batchUpdateAvailability.isPending
              }
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Crew List Remarks</CardTitle>
            <CardDescription className="text-xs">
              Shown on the crew list PDF export.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={crewListRemarks}
              onChange={(e) => setCrewListRemarks(e.target.value)}
              onBlur={() =>
                saveProjectRemarks("crew_list_remarks", crewListRemarks)
              }
              placeholder="Remarks for the crew list..."
              rows={2}
            />
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="functions" className="min-w-0 space-y-4">
        <Card className="min-w-0">
          <CardHeader className="space-y-4 pb-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div data-testid="function-assignments-section">
                <CardTitle className="text-lg">Function Assignments</CardTitle>
                <CardDescription>
                  Create multi-day functions and assign people per day.
                </CardDescription>
              </div>
              <div className="hidden gap-2 sm:flex">
                <div className="flex min-w-0 gap-2">
                  <Input
                    className="h-9 w-[180px]"
                    placeholder="New group..."
                    value={newFunctionGroupName}
                    onChange={(event) =>
                      setNewFunctionGroupName(event.target.value)
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter") handleCreateFunctionGroup();
                    }}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    title="Add function group"
                    disabled={
                      !newFunctionGroupName.trim() ||
                      createFunctionGroup.isPending
                    }
                    onClick={handleCreateFunctionGroup}
                  >
                    <FolderPlus className="h-4 w-4" />
                  </Button>
                </div>
                {(functions || []).length === 0 && (
                  <Button
                    offlineEdit
                    variant="outline"
                    onClick={openCopyFunctionsDialog}
                    data-testid="copy-functions-from-button"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy from
                  </Button>
                )}
                <InitializeProjectFunctionsDialog
                  projectId={project.id}
                  planningDays={planningDays}
                  eventDays={eventDays}
                  existingFunctions={functions || []}
                  projectFunctionGroups={functionGroups || []}
                  onInitialize={handleInitializeProjectFunctions}
                  onUpdateSeries={handleUpdateSeries}
                  onUpdateDayOverride={handleUpdateDayOverride}
                  isLoading={initializeProjectFunctions.isPending}
                />
                <AddFunctionDialog
                  planningDays={planningDays}
                  eventDays={eventDays}
                  groups={functionGroups || []}
                  onAdd={handleAddFunction}
                  isLoading={createFunctionSeries.isPending}
                />
              </div>
              <div className="sm:hidden">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72 p-2">
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          className="h-9"
                          placeholder="New group..."
                          value={newFunctionGroupName}
                          onChange={(event) =>
                            setNewFunctionGroupName(event.target.value)
                          }
                          onKeyDown={(event) => {
                            if (event.key === "Enter")
                              handleCreateFunctionGroup();
                          }}
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          title="Add function group"
                          disabled={
                            !newFunctionGroupName.trim() ||
                            createFunctionGroup.isPending
                          }
                          onClick={handleCreateFunctionGroup}
                        >
                          <FolderPlus className="h-4 w-4" />
                        </Button>
                      </div>
                      <DropdownMenuSeparator />
                      {(functions || []).length === 0 && (
                        <Button
                          offlineEdit
                          variant="outline"
                          onClick={openCopyFunctionsDialog}
                          className="w-full justify-start"
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          Copy from
                        </Button>
                      )}
                      <InitializeProjectFunctionsDialog
                        projectId={project.id}
                        planningDays={planningDays}
                        eventDays={eventDays}
                        existingFunctions={functions || []}
                        projectFunctionGroups={functionGroups || []}
                        onInitialize={handleInitializeProjectFunctions}
                        onUpdateSeries={handleUpdateSeries}
                        onUpdateDayOverride={handleUpdateDayOverride}
                        isLoading={initializeProjectFunctions.isPending}
                        triggerClassName="w-full justify-start"
                      />
                      <AddFunctionDialog
                        planningDays={planningDays}
                        eventDays={eventDays}
                        groups={functionGroups || []}
                        onAdd={handleAddFunction}
                        isLoading={createFunctionSeries.isPending}
                        triggerClassName="w-full justify-start"
                      />
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <ToggleGroup
                type="single"
                value={functionViewMode}
                data-testid="function-view-toggle"
                onValueChange={(value) => {
                  if (value === "person" || value === "function") {
                    setFunctionViewMode(value);
                  }
                }}
              >
                <ToggleGroupItem
                  value="person"
                  data-testid="function-view-person"
                >
                  By person
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="function"
                  data-testid="function-view-function"
                >
                  By function
                </ToggleGroupItem>
              </ToggleGroup>
              <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                <ToggleGroup
                  type="single"
                  value={functionDayFilter}
                  data-testid="function-day-filter-toggle"
                  onValueChange={(value) => {
                    if (value === "planning" || value === "event") {
                      setFunctionDayFilter(value);
                    }
                  }}
                >
                  <ToggleGroupItem
                    value="planning"
                    data-testid="function-day-filter-planning"
                  >
                    Planning dates
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="event"
                    data-testid="function-day-filter-event"
                  >
                    Event dates
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <SendCrewEmailDialog
                availability={availability || []}
                projectName={projectDisplayName}
                projectId={project.id}
              />
              <CrewEmailHistory
                projectId={project.id}
                projectName={projectDisplayName}
                availability={availability || []}
              />
            </div>
          </CardHeader>
          <CardContent className="min-w-0">
            <FunctionAssignmentMatrix
              functions={functions || []}
              groups={functionGroups || []}
              travelDays={travelDays || []}
              availability={availability || []}
              planningDays={planningDays}
              eventDays={eventDays}
              visibleDays={visibleFunctionDays}
              viewMode={functionViewMode}
              onAssign={handleAssign}
              onBulkAssign={handleBulkAssign}
              onBookAssignedPerson={handleBookAssignedPerson}
              onUnassign={handleUnassign}
              onUpdateRemark={handleUpdateRemark}
              onUpdateSeries={handleUpdateSeries}
              onDeleteSeries={handleDeleteSeries}
              onUpdateDayOverride={handleUpdateDayOverride}
              onUpdateGroup={handleUpdateFunctionGroup}
              onCreateFunction={handleAddFunction}
              onToggleTravelDay={handleToggleTravelDay}
              cameraNumberByAssignmentId={cameraAssignmentMap}
              isUpdating={
                createAssignment.isPending ||
                bulkAssignSeries.isPending ||
                updateFunctionSeries.isPending ||
                updateFunctionDay.isPending
              }
            />
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="planning" className="min-w-0 space-y-4">
        <Card className="min-w-0">
          <CardHeader>
            <CardTitle className="text-lg">Meal & Hotel Planning</CardTitle>
            <CardDescription>
              Plan meals (L=Lunch, D=Dinner) and hotel rooms (S=Single,
              DBL=Double) per person per day.
            </CardDescription>
          </CardHeader>
          <CardContent className="min-w-0">
            <MealHotelPlanningTable
              availability={availability || []}
              functions={functions || []}
              projectDays={planningDays}
              mealPlans={mealPlans || []}
              hotelPlans={hotelPlans || []}
              onUpdateMeal={handleUpdateMeal}
              onUpdateHotel={handleUpdateHotel}
              onInitFromFunctions={handleInitFromFunctions}
              isUpdating={upsertMealPlan.isPending || upsertHotelPlan.isPending}
            />
          </CardContent>
        </Card>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Meals Remarks</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={mealsRemarks}
                onChange={(e) => setMealsRemarks(e.target.value)}
                onBlur={() => saveProjectRemarks("meals_remarks", mealsRemarks)}
                placeholder="Remarks about meals..."
                rows={2}
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Hotels Remarks</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={hotelsRemarks}
                onChange={(e) => setHotelsRemarks(e.target.value)}
                onBlur={() =>
                  saveProjectRemarks("hotels_remarks", hotelsRemarks)
                }
                placeholder="Remarks about hotels..."
                rows={2}
              />
            </CardContent>
          </Card>
        </div>
      </TabsContent>

      <TabsContent value="intercom">
        <IntercomTab projectId={project.id} studioId={project.studio_id} />
      </TabsContent>

      <Dialog open={copyFunctionsOpen} onOpenChange={setCopyFunctionsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5" />
              Copy from
            </DialogTitle>
            <DialogDescription>
              Select a project to copy function series and assignments from.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Project</label>
            <CopyFromProjectSelector
              value={sourceProjectId}
              onChange={setSourceProjectId}
              currentProjectId={project.id}
              placeholder="Select source project"
              testId="copy-functions-source-project"
            />
            {sourceProjectId && !loadingSourceFunctions ? (
              <p className="text-xs text-muted-foreground">
                {sourceFunctions.length} function
                {sourceFunctions.length === 1 ? "" : "s"} will be copied.
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCopyFunctionsOpen(false)}
            >
              Cancel
            </Button>
            <Button
              offlineEdit
              onClick={handleCopyFunctions}
              disabled={
                !sourceProjectId ||
                loadingSourceFunctions ||
                copyProjectFunctions.isPending
              }
            >
              Copy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Tabs>
  );
}
